import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

import { useLollipopFooterOffset } from './useLollipopFooterOffset';

export const LOLLIPOP_ZINDEX = 1;
const STEM_WIDTH = 1;

export const VERTICAL_LINE_SIZE = 12;

type WrapperProps = {
  increaseZIndex: boolean;
  lollipopPosition: number;
  showLollipopFooter?: boolean;
  footerOffset: number;
  footerWidth: number;
  showStem?: boolean;
};

const LollipopWrapper = styled.span<WrapperProps>`
  bottom: ${({ showStem }) => (showStem ? tokens.spacer.x2 : tokens.spacer.x0)};
  left: ${(props) => props.lollipopPosition}%;
  position: absolute;
  transform: translateX(-50%);
  z-index: ${({ increaseZIndex }) => (increaseZIndex ? LOLLIPOP_ZINDEX : 0)};
  cursor: pointer;

  &:hover {
    z-index: ${LOLLIPOP_ZINDEX};
    margin-top: 0;
    padding-bottom: 0;
  }

  ${({ showStem, showLollipopFooter, footerOffset, footerWidth }) =>
    showStem
      ? `
    // NOTE: stem
    &::before {
      content: '';
      position: absolute;
      border-left: 1px solid ${tokens.color.azure};
      height: ${VERTICAL_LINE_SIZE}px;
      left: 50%;
      top: 100%;
    }
    &:hover::before {
      height: ${VERTICAL_LINE_SIZE}px;
      top: 100%;
    }

    // NOTE: horizontal line indicating clip duration or just being there for comments
    &::after {
      background-color: ${tokens.color.azure};
      border-radius: 2px;
      content: '';
      height: ${showLollipopFooter ? 4 : STEM_WIDTH}px;
      position: absolute;
      top: calc(100% + ${VERTICAL_LINE_SIZE}px);
      transform: ${`translateX(calc(-50% + ${footerOffset}px))`};
      width: ${footerWidth}px;
    }
  `
      : null}
`;

interface LollipopProps {
  increaseZIndex?: boolean;
  showLollipopFooter?: boolean;
  showStem?: boolean;
  children: React.ReactNode;
  position: number;
  timelineWidth: number;
  className?: string;
}

export const Lollipop = forwardRef<HTMLSpanElement, LollipopProps>(
  (
    {
      children,
      increaseZIndex = false,
      showLollipopFooter,
      showStem,
      position,
      timelineWidth,
      className,
    },
    ref,
  ): JSX.Element => {
    const { footerOffset, footerWidth } = useLollipopFooterOffset({
      maxAbsolutePosition: timelineWidth,
      percentagePosition: position,
      showLollipopFooter,
    });

    return (
      <LollipopWrapper
        className={className}
        increaseZIndex={increaseZIndex}
        lollipopPosition={position}
        showLollipopFooter={showLollipopFooter}
        footerOffset={footerOffset}
        footerWidth={footerWidth}
        ref={ref}
        showStem={showStem}
      >
        {children}
      </LollipopWrapper>
    );
  },
);
